import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useField } from "formik";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  ErrorMessage,
  Flex,
  LoadingIndicator,
  ModalV2,
  Text,
  TypeaheadInputComponent,
} from "@jsq/ds/core";
import { FormCheckbox, FormSelect, useFormState } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import { useGetClientNameByIdLazyQuery } from "~/graphql/mainHooks";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import {
  useComplianceEntitiesLazyQuery,
  useGetComplianceClientsSliceLazyQuery,
  useGetProgramNamesAndIdsQuery,
  useOnboardingCaseForSubjectLazyQuery,
} from "~/graphql/supergraphHooks";
import { toDbId } from "~/utils/graphql-helpers";
import { useEntityClientWizard } from "../../context";
import { ArenaTypeahead, type ArenaOption } from "../ArenaTypeahead";
import { buildLinkExternalObject } from "../buildExternalObject";
import { EntityTypeahead, type EntityOption } from "../EntityTypeahead";
import * as styles from "../styles_vanilla.css";
import {
  caseInfoPrompt,
  entityNameSliceFilter,
  findClientSliceRow,
  findEntitySliceRow,
  programSectionCopy,
  unlinkedChipLabel,
} from "../utils";
import type { CaseInfoFormValues } from "../schema";

export const CaseInfoFields = () => {
  const { values, setFieldValue } = useFormState<CaseInfoFormValues>();
  const history = useHistory();
  const { caseType, setProductLine, setProductLineLoading } = useEntityClientWizard();
  const arenaRef = useRef<TypeaheadInputComponent>(null);
  const entityRef = useRef<TypeaheadInputComponent>(null);
  const [existingCaseId, setExistingCaseId] = useState<number | null>(null);

  const [, clientMeta] = useField<string>("clientId");
  const [, entityMeta] = useField<string>("entityId");

  const programsResult = useGetProgramNamesAndIdsQuery({ fetchPolicy: "cache-and-network" });
  const programs = useMemo(
    () => programsResult.data?.programsAssignableToEntities ?? [],
    [programsResult.data?.programsAssignableToEntities],
  );

  const [checkExistingCase] = useOnboardingCaseForSubjectLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getComplianceClients] = useGetComplianceClientsSliceLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getClientNameById] = useGetClientNameByIdLazyQuery({
    fetchPolicy: "network-only",
  });
  const [getComplianceEntities] = useComplianceEntitiesLazyQuery({
    fetchPolicy: "no-cache",
  });
  const preCheckGeneration = useRef(0);
  const productLineGeneration = useRef(0);
  const entityProgramGeneration = useRef(0);
  const lookupsInFlight = useRef(0);
  const didHydrateFromIds = useRef(false);
  const clientProductLineRef = useRef<string | undefined>(undefined);
  const applyLinkedProductLineRef = useRef(false);
  const [idsReady, setIdsReady] = useState(() => !values.clientId && !values.entityId);

  const resetDuplicatePreCheck = useCallback(() => {
    preCheckGeneration.current += 1;
    setExistingCaseId(null);
  }, []);

  const clearLinkedProductLine = useCallback(() => {
    clientProductLineRef.current = undefined;
    applyLinkedProductLineRef.current = false;
    setProductLine(undefined);
    setProductLineLoading(false);
  }, [setProductLine, setProductLineLoading]);

  const trackLookup = useCallback(
    async (task: () => Promise<void>) => {
      lookupsInFlight.current += 1;
      setProductLineLoading(true);
      try {
        await task();
      } finally {
        lookupsInFlight.current -= 1;
        if (lookupsInFlight.current === 0) setProductLineLoading(false);
      }
    },
    [setProductLineLoading],
  );

  const loadClientProductLine = useCallback(
    async (clientId: string, clientName: string) => {
      const generation = productLineGeneration.current;
      await trackLookup(async () => {
        try {
          const arenaId = toDbId(clientId);
          const { data } = await getComplianceClients({
            variables: {
              offset: 0,
              limit: 20,
              filters: [{ field: "client", filterValues: [clientName] }],
            },
          });
          if (productLineGeneration.current !== generation) return;
          const row = findClientSliceRow(data?.complianceClientsSlice.rows, arenaId);
          const productLine = row?.productLine || undefined;
          clientProductLineRef.current = productLine;
          if (applyLinkedProductLineRef.current) setProductLine(productLine);
          if (caseType === "client") {
            const programName = row?.clientProgram ?? "";
            await setFieldValue("programName", programName);
            const match = programs.find((p) => p.name === programName);
            await setFieldValue("programId", match?.id);
          }
        } catch {
          if (productLineGeneration.current !== generation) return;
          clientProductLineRef.current = undefined;
          if (applyLinkedProductLineRef.current) setProductLine(undefined);
        }
      });
    },
    [getComplianceClients, setProductLine, trackLookup, caseType, programs, setFieldValue],
  );

  const loadEntityProgram = useCallback(
    async (
      clientId: string,
      entityId: string,
      entityName?: string,
    ): Promise<string | undefined> => {
      const generation = entityProgramGeneration.current;
      let resolvedName: string | undefined;
      await trackLookup(async () => {
        try {
          const arenaId = toDbId(clientId);
          const entityDbId = toDbId(entityId);
          const fetchRows = async (nameHint?: string) => {
            const { data } = await getComplianceEntities({
              variables: {
                arenaId,
                offset: 0,
                limit: 100,
                filters: nameHint ? [entityNameSliceFilter(nameHint)] : undefined,
              },
            });
            return data?.complianceEntitiesSlice ?? [];
          };
          let row = findEntitySliceRow(await fetchRows(entityName), entityDbId);
          if (!row && entityName) {
            row = findEntitySliceRow(await fetchRows(), entityDbId);
          }
          if (entityProgramGeneration.current !== generation) return;
          if (!row) {
            await setFieldValue("programName", "");
            await setFieldValue("programId", undefined);
            return;
          }
          resolvedName = row.name;
          await setFieldValue("entityName", row.name);
          const programName = row.entityProgram ?? "";
          await setFieldValue("programName", programName);
          const match = programs.find((p) => p.name === programName);
          await setFieldValue("programId", match?.id);
          if (applyLinkedProductLineRef.current) setProductLine(clientProductLineRef.current);
        } catch {
          if (entityProgramGeneration.current !== generation) return;
          await setFieldValue("programName", "");
          await setFieldValue("programId", undefined);
        }
      });
      return resolvedName;
    },
    [getComplianceEntities, programs, setFieldValue, setProductLine, trackLookup],
  );

  const clearEntityFields = useCallback(() => {
    resetDuplicatePreCheck();
    entityProgramGeneration.current += 1;
    applyLinkedProductLineRef.current = false;
    void setFieldValue("entityId", undefined);
    void setFieldValue("entityName", undefined);
    void setFieldValue("isUnlinked", false);
    void setFieldValue("programName", "");
    void setFieldValue("programId", undefined);
    setProductLine(undefined);
  }, [resetDuplicatePreCheck, setFieldValue, setProductLine]);

  const runDuplicatePreCheck = useCallback(
    async (args: {
      subjectKind: "entity" | "client";
      entityId?: string;
      entityName?: string;
      clientId?: string;
      clientName?: string;
    }) => {
      const generation = preCheckGeneration.current;
      const externalObject = buildLinkExternalObject(args);
      if (!externalObject) return;
      try {
        const { data, error } = await checkExistingCase({
          variables: {
            caseType:
              args.subjectKind === "entity"
                ? ComplianceCaseTypeEnum.EntityOnboarding
                : ComplianceCaseTypeEnum.ClientOnboarding,
            externalObject,
          },
        });
        if (preCheckGeneration.current !== generation) return;
        if (error) return;
        const caseId = data?.onboardingCaseForSubject?.id;
        if (caseId) setExistingCaseId(caseId);
      } catch {
        // Do not block case creation if the pre-check fails.
      }
    },
    [checkExistingCase],
  );

  const handleSelectClient = useCallback(
    (opt: ArenaOption) => {
      productLineGeneration.current += 1;
      clientProductLineRef.current = undefined;
      if (opt.isUnlinked) {
        resetDuplicatePreCheck();
        clearLinkedProductLine();
        void setFieldValue("clientId", undefined);
        void setFieldValue("clientName", opt.label);
        void setFieldValue("entityId", undefined);
        void setFieldValue("entityName", undefined);
        void setFieldValue("isUnlinked", true);
        void setFieldValue("programName", "");
        void setFieldValue("programId", undefined);
        entityRef.current?.clearInputValue();
        return;
      }
      void setFieldValue("clientId", opt.id);
      void setFieldValue("clientName", opt.label);
      clearEntityFields();
      applyLinkedProductLineRef.current = caseType === "client";
      entityRef.current?.clearInputValue();
      void loadClientProductLine(opt.id, opt.label);
      if (caseType === "client") {
        void runDuplicatePreCheck({
          subjectKind: "client",
          clientId: opt.id,
          clientName: opt.label,
        });
      }
    },
    [
      setFieldValue,
      clearEntityFields,
      clearLinkedProductLine,
      resetDuplicatePreCheck,
      caseType,
      loadClientProductLine,
      runDuplicatePreCheck,
    ],
  );

  const handleSelectEntity = useCallback(
    (opt: EntityOption) => {
      resetDuplicatePreCheck();
      entityProgramGeneration.current += 1;
      if (opt.isUnlinked) {
        applyLinkedProductLineRef.current = false;
        void setFieldValue("entityId", undefined);
        void setFieldValue("entityName", opt.label);
        void setFieldValue("isUnlinked", true);
        void setFieldValue("programName", "");
        void setFieldValue("programId", undefined);
        setProductLine(undefined);
        setProductLineLoading(false);
        return;
      }
      applyLinkedProductLineRef.current = true;
      void setFieldValue("entityId", opt.id);
      void setFieldValue("entityName", opt.label);
      void setFieldValue("isUnlinked", false);
      void setFieldValue("programName", "");
      void setFieldValue("programId", undefined);
      setProductLine(clientProductLineRef.current);
      if (values.clientId) {
        void loadEntityProgram(values.clientId, opt.id, opt.label);
      }
      void runDuplicatePreCheck({
        subjectKind: "entity",
        entityId: opt.id,
        entityName: opt.label,
        clientId: values.clientId,
        clientName: values.clientName,
      });
    },
    [
      resetDuplicatePreCheck,
      setFieldValue,
      setProductLine,
      setProductLineLoading,
      loadEntityProgram,
      runDuplicatePreCheck,
      values.clientId,
      values.clientName,
    ],
  );

  const handleClearClient = useCallback(() => {
    productLineGeneration.current += 1;
    clearLinkedProductLine();
    void setFieldValue("clientId", undefined);
    void setFieldValue("clientName", undefined);
    clearEntityFields();
    entityRef.current?.clearInputValue();
  }, [clearLinkedProductLine, setFieldValue, clearEntityFields]);

  const handleProgramChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const name = typeof value === "string" ? value : String(value);
      const match = programs.find((p) => p.name === name);
      void setFieldValue("programName", name);
      void setFieldValue("programId", match?.id);
    },
    [programs, setFieldValue],
  );

  const handleViewExistingCase = useCallback(() => {
    if (!existingCaseId) return;
    setExistingCaseId(null);
    history.push(`${globalEnv.complianceRoute}/case/${existingCaseId}`);
  }, [history, existingCaseId]);

  const hydrateFromSearchIds = useCallback(async () => {
    const clientId = values.clientId;
    const entityId = values.entityId;
    if (!clientId && !entityId) {
      setIdsReady(true);
      return;
    }

    let resolvedClientName = values.clientName ?? "";
    if (clientId) {
      await trackLookup(async () => {
        try {
          const { data } = await getClientNameById({
            variables: { arenaIds: [clientId] },
            context: withClientContext("staff"),
          });
          const name = data?.arenas?.edges?.[0]?.node?.name;
          if (name) {
            resolvedClientName = name;
            await setFieldValue("clientName", name);
          }
        } catch {
          return;
        }
      });
      applyLinkedProductLineRef.current = caseType === "client" || Boolean(entityId);
      if (resolvedClientName) {
        void loadClientProductLine(clientId, resolvedClientName);
      }
      if (caseType === "client") {
        void runDuplicatePreCheck({
          subjectKind: "client",
          clientId,
          clientName: resolvedClientName,
        });
      }
    }

    if (caseType === "entity" && clientId && entityId) {
      applyLinkedProductLineRef.current = true;
      const resolvedEntityName = await loadEntityProgram(clientId, entityId, values.entityName);
      void runDuplicatePreCheck({
        subjectKind: "entity",
        entityId,
        entityName: resolvedEntityName ?? values.entityName,
        clientId,
        clientName: resolvedClientName,
      });
    }

    setIdsReady(true);
  }, [
    values.clientId,
    values.entityId,
    values.clientName,
    values.entityName,
    caseType,
    getClientNameById,
    setFieldValue,
    trackLookup,
    loadClientProductLine,
    loadEntityProgram,
    runDuplicatePreCheck,
  ]);

  useEffect(() => {
    if (didHydrateFromIds.current) return;
    didHydrateFromIds.current = true;
    void hydrateFromSearchIds();
  }, [hydrateFromSearchIds]);

  useEffect(() => {
    if (!values.programName || values.programId != null) return;
    const match = programs.find((program) => program.name === values.programName);
    if (match) void setFieldValue("programId", match.id);
  }, [programs, values.programName, values.programId, setFieldValue]);

  const unlinkedChip = (
    <Flex className={styles.unlinkedChip} data-testid="unlinked-case-chip">
      <Icon type="unlink" color={Colors.Neutral600} />
      <Text variant="secondary-sm" asSpan>
        {unlinkedChipLabel(caseType)}
      </Text>
    </Flex>
  );

  return (
    <>
      <Card title="Case information">
        <Text variant="regular-md" className={styles.promptText}>
          {caseInfoPrompt(caseType)}
        </Text>

        {idsReady ? (
          <Flex className={styles.clientEntityRow} gap={Sizes.LG} flexDirection="row">
            <Flex flexDirection="column">
              <ArenaTypeahead
                onSelect={handleSelectClient}
                onClear={handleClearClient}
                inputRef={arenaRef}
                allowUnlinked={caseType === "client"}
                placeholder={
                  values.clientId || (caseType === "client" && values.isUnlinked)
                    ? values.clientName
                    : ""
                }
                initialSearchValue={values.clientName ?? ""}
              />
              {clientMeta.touched && clientMeta.error && (
                <ErrorMessage>{clientMeta.error}</ErrorMessage>
              )}
              {values.isUnlinked && caseType === "client" && unlinkedChip}
            </Flex>
            {caseType === "entity" && (
              <Flex flexDirection="column">
                <EntityTypeahead
                  arenaId={values.clientId || undefined}
                  onSelect={handleSelectEntity}
                  onClear={clearEntityFields}
                  inputRef={entityRef}
                  placeholder={values.entityId || values.isUnlinked ? values.entityName : ""}
                  initialSearchValue={values.entityName ?? ""}
                />
                {entityMeta.touched && entityMeta.error && (
                  <ErrorMessage>{entityMeta.error}</ErrorMessage>
                )}
                {values.isUnlinked && unlinkedChip}
              </Flex>
            )}
          </Flex>
        ) : (
          <Flex
            className={styles.clientEntityRow}
            justifyContent="center"
            alignItems="center"
            data-testid="case-info-id-hydration"
          >
            <LoadingIndicator />
          </Flex>
        )}

        {values.isUnlinked && (
          <div className={styles.programSection} data-testid="compliance-program-section">
            <Text variant="regular-md" className={styles.programSectionText}>
              {programSectionCopy(caseType, values.clientName ?? "")}
            </Text>
            <FormSelect
              name="programName"
              label="Compliance program"
              placeholder="Select a compliance program"
              full
              hasLeftPadding={false}
              handleChange={handleProgramChange}
              data-testid="compliance-program-select"
            >
              <FormSelect.Section>
                {programs.map((program) => (
                  <FormSelect.Option key={program.id} value={program.name}>
                    {program.name}
                  </FormSelect.Option>
                ))}
              </FormSelect.Section>
            </FormSelect>
          </div>
        )}

        <FormCheckbox name="assignToMyself" label="Assign case to myself" />
      </Card>

      <ModalV2
        show={!!existingCaseId}
        closeButton
        closeOnClickOutside={false}
        escapeToClose={false}
        onHide={() => setExistingCaseId(null)}
        title="Case Already Exists"
        size="small"
        data-testid="case-exists-modal"
      >
        <ModalV2.Body>
          A compliance case already exists for this{" "}
          {caseType === "client" ? "client" : "entity"}.
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap="md">
            <Button variant="primary" onClick={handleViewExistingCase}>
              View case
            </Button>
          </Flex>
        </ModalV2.Footer>
      </ModalV2>
    </>
  );
};
